import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useSidebar } from "../../contexts/SidebarContext";
import { SettingsAPI, ClinicExportJob } from "../../services/settings.service";
import { StatusBadge } from "../../components/ui/StatusBadge";
import {
  CheckCircleIcon,
  ClockIcon,
  DownloadIcon,
  RefreshIcon,
  SpinnerIcon,
  WarningTriangleIcon,
  XCircleIcon,
} from "../../components/icons/exportIcons";
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatTimeRemaining,
  scopeLabel,
  statusToBadgeVariant,
} from "../../utils/clinicExport";

const TABLE_FILES: { key: string; file: string }[] = [
  { key: "patients", file: "patients.csv" },
  { key: "visits", file: "visits.csv" },
  { key: "soap_notes", file: "soap_notes.csv" },
  { key: "vitals", file: "vitals.csv" },
  { key: "prescriptions", file: "prescriptions.csv" },
  { key: "appointments", file: "appointments.csv" },
  { key: "invoices", file: "invoices.csv" },
  { key: "doctors", file: "doctors.csv" },
  { key: "clinic_documents", file: "documents.csv" },
];

const SECONDARY_BTN =
  "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition";
const PRIMARY_BTN =
  "inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 transition";

export const ExportJobStatusPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { isExpanded } = useSidebar();
  const [job, setJob] = useState<ClinicExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(() => {
    if (!jobId) return;
    SettingsAPI.getClinicExportStatus(jobId)
      .then(setJob)
      .catch(() => setError("Could not load this export job."));
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (job && (job.status === "pending" || job.status === "running")) {
      pollRef.current = setInterval(load, 4000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
    return undefined;
  }, [job, load]);

  const handleCancel = async () => {
    if (!jobId) return;
    await SettingsAPI.cancelClinicExport(jobId);
    load();
  };

  const handleReissue = () => {
    load();
  };

  if (error) {
    return (
      <PageShell isExpanded={isExpanded}>
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
          {error}
        </p>
      </PageShell>
    );
  }

  if (!job) {
    return (
      <PageShell isExpanded={isExpanded}>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <SpinnerIcon /> Loading export…
        </div>
      </PageShell>
    );
  }

  const doneTables = job.row_count_manifest ? Object.keys(job.row_count_manifest) : [];
  const percent = Math.round((doneTables.length / TABLE_FILES.length) * 100);

  return (
    <PageShell isExpanded={isExpanded}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Data Export</h2>
          <p className="text-sm text-gray-500 mt-1">
            Job {job.job_id.slice(0, 8).toUpperCase()} ·{" "}
            {job.status === "completed" && job.started_at && job.completed_at
              ? `completed in ${formatDuration(job.started_at, job.completed_at)}`
              : job.status === "failed" && job.started_at && job.completed_at
                ? `stopped after ${formatDuration(job.started_at, job.completed_at)}`
                : `started ${formatDateTime(job.started_at ?? job.requested_at)}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge variant={statusToBadgeVariant(job.status)} label={job.status} />
          {(job.status === "pending" || job.status === "running") && (
            <button className={SECONDARY_BTN} onClick={handleCancel}>
              <XCircleIcon /> Cancel Export
            </button>
          )}
          {job.status === "completed" && (
            <button className={SECONDARY_BTN} onClick={handleReissue}>
              <RefreshIcon /> Re-issue Link
            </button>
          )}
        </div>
      </div>

      {(job.status === "pending" || job.status === "running") && (
        <RunningView job={job} doneTables={doneTables} percent={percent} />
      )}
      {job.status === "completed" && <ReadyView job={job} />}
      {(job.status === "failed" || job.status === "cancelled") && (
        <FailedView job={job} onRetry={() => navigate("/settings?section=privacy")} />
      )}
    </PageShell>
  );
};

const PageShell: React.FC<{ isExpanded: boolean; children: React.ReactNode }> = ({
  isExpanded,
  children,
}) => (
  <div className="flex h-screen bg-gray-50 overflow-hidden">
    <Sidebar />
    <div className="flex-1 flex overflow-hidden pt-14 lg:pt-0">
      <div
        className="flex-shrink-0 hidden lg:block transition-all duration-300"
        style={{ width: isExpanded ? 240 : 72 }}
      />
      <div className="flex-1 overflow-y-auto p-6 lg:p-10">
        <div className="max-w-5xl mx-auto">{children}</div>
      </div>
    </div>
  </div>
);

const RunningView: React.FC<{
  job: ClinicExportJob;
  doneTables: string[];
  percent: number;
}> = ({ job, doneTables, percent }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <SpinnerIcon className="text-blue-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Preparing Your Archive</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Writing one file per table, then copying documents from storage.
            </p>
          </div>
        </div>
        <span className="text-3xl font-semibold text-gray-900">{percent}%</span>
      </div>

      <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-blue-600 transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="mt-5 border-t border-gray-100">
        {TABLE_FILES.map(t => {
          const done = doneTables.includes(t.key);
          const isNext = !done && doneTables.length === TABLE_FILES.indexOf(t);
          return (
            <div
              key={t.key}
              className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0 text-sm"
            >
              <span className="flex items-center gap-2 text-gray-700">
                {done ? (
                  <CheckCircleIcon className="text-green-600 w-4 h-4" />
                ) : isNext ? (
                  <SpinnerIcon className="text-blue-600 w-4 h-4" />
                ) : (
                  <ClockIcon className="text-gray-300 w-4 h-4" />
                )}
                {t.file}
              </span>
              <span className={done ? "text-gray-500" : isNext ? "text-blue-600" : "text-gray-400"}>
                {done
                  ? job.row_count_manifest?.[t.key]?.toLocaleString()
                  : isNext
                    ? "in progress"
                    : "queued"}
              </span>
            </div>
          );
        })}
        {job.include_documents && doneTables.length === TABLE_FILES.length && (
          <div className="flex items-center justify-between py-2.5 text-sm">
            <span className="flex items-center gap-2 text-blue-600">
              <SpinnerIcon className="w-4 h-4" /> documents (copied as uploaded)
            </span>
            <span className="text-blue-600">in progress</span>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-4">
        The zip is published only after every table and file is written — an interrupted run
        never presents a partial archive.
      </p>
    </div>

    <div className="space-y-4">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
        <p className="text-sm font-medium text-blue-800">You can leave this page.</p>
        <p className="text-sm text-blue-700 mt-1">
          Kaagazy emails you when the archive is ready. The download link is valid for 48 hours
          from that email.
        </p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Job Details</h4>
        <dl className="space-y-2 text-sm">
          <Row label="Job id" value={job.job_id} mono />
          <Row label="Requested by" value={job.requested_by_name ?? "—"} />
          <Row label="Scope" value={scopeLabel(job.include_documents)} />
          <Row label="Started" value={formatDateTime(job.started_at)} />
          <Row label="Status" value={`writing tables (${doneTables.length} of ${TABLE_FILES.length})`} />
        </dl>
        <p className="text-xs text-gray-400 mt-3">
          Only one export runs at a time for a clinic. A second request is blocked until this job
          finishes.
        </p>
      </div>
    </div>
  </div>
);

const ReadyView: React.FC<{ job: ClinicExportJob }> = ({ job }) => {
  const manifest = job.row_count_manifest ?? {};
  const totalRows = Object.values(manifest).reduce((s, n) => s + n, 0);
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white flex-shrink-0">
            <CheckCircleIcon />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Your Archive Is Ready</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {formatBytes(job.archive_size_bytes)} · {Object.keys(manifest).length} tables ·{" "}
              {totalRows.toLocaleString()} rows
            </p>
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5" /> Link expires in{" "}
              {formatTimeRemaining(job.download_url_expires_at)}
              {job.download_url_expires_at ? ` — ${formatDateTime(job.download_url_expires_at)}` : ""}
              {" · issued to " + (job.requested_by_name ?? "you")} · audit-logged
            </p>
          </div>
        </div>
        <a
          href={job.download_url ?? "#"}
          className={PRIMARY_BTN}
          onClick={e => {
            if (!job.download_url) e.preventDefault();
          }}
        >
          <DownloadIcon /> Download Archive
        </a>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-gray-900">Manifest</h3>
          <span className="text-xs text-gray-500">
            {Object.keys(manifest).length} tables · {totalRows.toLocaleString()} rows · verified
            against live tables at {formatDateTime(job.completed_at)}
          </span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-5 py-3">Table</th>
              <th className="px-5 py-3">File</th>
              <th className="px-5 py-3">Rows Exported</th>
              <th className="px-5 py-3">Verified</th>
            </tr>
          </thead>
          <tbody>
            {TABLE_FILES.filter(t => manifest[t.key] !== undefined).map(t => (
              <tr key={t.key} className="border-b border-slate-50 last:border-0">
                <td className="px-5 py-3 text-gray-700">{t.key}</td>
                <td className="px-5 py-3 text-gray-500">{t.file}</td>
                <td className="px-5 py-3 text-gray-700">{manifest[t.key].toLocaleString()}</td>
                <td className="px-5 py-3">
                  <StatusBadge variant="match" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 px-5 py-4 border-t border-gray-50">
          {job.include_documents
            ? "Original documents were copied as uploaded into the archive's files/ folder. "
            : ""}
          Aadhaar numbers are masked in every file; payment records carry account id and status
          only.
        </p>
      </div>
    </div>
  );
};

const FailedView: React.FC<{ job: ClinicExportJob; onRetry: () => void }> = ({ job, onRetry }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-10 flex flex-col items-center text-center">
      <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
        <WarningTriangleIcon className="w-7 h-7" />
      </div>
      <h3 className="text-base font-semibold text-gray-900">
        {job.status === "cancelled" ? "Export Cancelled" : "Export Could Not Be Completed"}
      </h3>
      <p className="text-sm text-gray-500 mt-2 max-w-md">
        {job.status === "cancelled"
          ? "This export was cancelled. Your clinic's records are untouched and nothing was deleted."
          : "The job stopped before an archive was published. Your clinic's records are untouched and nothing was deleted."}
      </p>
      <div className="flex items-center gap-3 mt-6">
        <button className={PRIMARY_BTN} onClick={onRetry}>
          <RefreshIcon /> {job.status === "cancelled" ? "Start New Export" : "Retry Export"}
        </button>
        <button className={SECONDARY_BTN}>Contact Support</button>
      </div>
    </div>

    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Failure Details</h4>
        <dl className="space-y-2 text-sm">
          <Row label="Job id" value={job.job_id} mono />
          <Row label="Reason" value={job.error_detail ?? "—"} />
          <Row label="Tables written" value={`${Object.keys(job.row_count_manifest ?? {}).length} (discarded with the run)`} />
          <Row label="Failed at" value={formatDateTime(job.completed_at)} />
        </dl>
      </div>
      {job.status !== "cancelled" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">Failure notice sent.</p>
          <p className="text-sm text-amber-700 mt-0.5">
            Kaagazy emailed {job.requested_by_name ?? "the requester"}. The failure is
            audit-logged.
          </p>
        </div>
      )}
    </div>
  </div>
);

const Row: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-center justify-between">
    <dt className="text-gray-500">{label}</dt>
    <dd className={`text-gray-800 text-right ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
  </div>
);
